import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchCoursesWithFiltersAsyncThunk } from './courseThunk';
import { clearError, setFilters, setCurrentPage, setItemsPerPage } from './courseReducer';
import { useDebounce } from '../../utils/useDebounce';
import SearchInput from '../../components/SearchInput';
import Pagination from '../../components/Pagination';
import { openModal } from '../common/modalSlice';
import { MODAL_BODY_TYPES } from '../../utils/globalConstantUtil';

function Courses() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { courses, pagination, isLoading, filters } = useSelector((state) => state.course);
  
  // Local search state to prevent focus loss
  const [localSearchTerm, setLocalSearchTerm] = useState(filters.search || '');
  const searchInputRef = useRef(null);
  const [shouldRestoreFocus, setShouldRestoreFocus] = useState(false);
  
  // Debounced search term (500ms delay)
  const debouncedSearchTerm = useDebounce(localSearchTerm, 500);

  // Sync local search term with Redux filters when filters change externally
  useEffect(() => {
    setLocalSearchTerm(filters.search || '');
  }, [filters.search]);

  // Effect to handle debounced search
  useEffect(() => {
    if (debouncedSearchTerm !== filters.search) {
      // Check if search input is currently focused
      const isSearchFocused = document.activeElement === searchInputRef.current;
      if (isSearchFocused) {
        setShouldRestoreFocus(true);
      }
      
      dispatch(setFilters({ search: debouncedSearchTerm }));
      dispatch(setCurrentPage(1)); // Reset to first page when searching
      dispatch(fetchCoursesWithFiltersAsyncThunk());
    }
  }, [debouncedSearchTerm, dispatch, filters.search]);

  // Restore focus after component re-renders due to API calls
  useEffect(() => {
    if (shouldRestoreFocus && searchInputRef.current && !isLoading) {
      // Use setTimeout to ensure the DOM has been updated
      setTimeout(() => {
        searchInputRef.current?.focus();
        setShouldRestoreFocus(false);
      }, 0);
    }
  }, [shouldRestoreFocus, isLoading]);

  // Restore focus after any Redux state change that causes re-render
  useEffect(() => {
    if (shouldRestoreFocus && searchInputRef.current) {
      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      });
    }
  }, [courses, pagination, shouldRestoreFocus]);

  // Fetch data when pagination changes
  useEffect(() => {
    dispatch(fetchCoursesWithFiltersAsyncThunk());
  }, [dispatch, pagination.page, pagination.limit]);

  // Initial load
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Track when user is actively typing to maintain focus
  const handleSearchInputFocus = useCallback(() => {
    setShouldRestoreFocus(true);
  }, []);

  const handleSearchInputBlur = useCallback(() => {
    setShouldRestoreFocus(false);
  }, []);

  // Handle search input change (immediate local update)
  const handleSearch = useCallback((searchTerm) => {
    setLocalSearchTerm(searchTerm);
    // Don't dispatch Redux actions immediately - let the debounced effect handle it
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    dispatch(setCurrentPage(page));
  }, [dispatch]);

  const handlePageSizeChange = useCallback((itemsPerPage) => {
    dispatch(setItemsPerPage(itemsPerPage));
  }, [dispatch]);

  // Handle view details
  const handleViewDetails = useCallback((course) => {
    navigate(`/app/courses/${course._id}`);
  }, [navigate]);

  // Handle edit course
  const handleEditCourse = useCallback((id) => {
    dispatch(openModal({
      title: "Edit Course",
      bodyType: MODAL_BODY_TYPES.EDIT_COURSE,
      size: 'lg',
      extraObject: { id }
    }));
  }, [dispatch]);

  // Handle delete course
  const handleDeleteCourse = useCallback((course) => {
    dispatch(openModal({
      title: "Delete Course",
      bodyType: MODAL_BODY_TYPES.DELETE_COURSE_CONFIRMATION,
      size: 'md',
      extraObject: { course }
    }));
  }, [dispatch]);

  // Handle create course
  const handleCreateCourse = useCallback(() => {
    dispatch(openModal({
      title: "Create New Course",
      bodyType: MODAL_BODY_TYPES.CREATE_COURSE,
      size: 'lg',
      extraObject: {}
    }));
  }, [dispatch]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  const formatDuration = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
  };

  const getLevelColor = (level) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'badge-success';
      case 'intermediate':
        return 'badge-warning';
      case 'advanced':
        return 'badge-error';
      default:
        return 'badge-info';
    }
  };

  const getFormatColor = (format) => {
    switch (format.toLowerCase()) {
      case 'on-demand':
        return 'badge-primary';
      case 'live':
        return 'badge-secondary';
      case 'hybrid':
        return 'badge-accent';
      default:
        return 'badge-neutral';
    }
  };

  if (isLoading && courses.length === 0) {
    return (
      <div className="min-h-screen bg-base-200 py-8">
        <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-3">
          <div className="flex items-center justify-center h-64">
            <div className="loading loading-spinner loading-lg text-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-3">
        {/* Header */}
        <div className="bg-base-100 rounded-xl shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-base-content mb-2">Courses</h1>
              <p className="text-base-content/70">Manage your course catalog and content</p>
            </div>
            <div className="flex gap-3">
              <button 
                className="btn btn-outline btn-primary"
                onClick={() => navigate('/app/authors')}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Manage Authors
              </button>
              <button 
                className="btn btn-primary text-base-100"
                onClick={handleCreateCourse}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create Course
              </button>
            </div>
          </div>
          
          {/* Search Input and Filters */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 min-w-0">
            <div className="w-full flex-1">
              <SearchInput
                value={localSearchTerm}
                onChange={handleSearch}
                onFocus={handleSearchInputFocus}
                onBlur={handleSearchInputBlur}
                inputRef={searchInputRef}
                placeholder="Search courses..."
              />
            </div>
            
            <div className="flex bg-base-200 p-1 rounded-lg">
              <button 
                className={`btn btn-sm border-none ${filters.isActive === true ? 'bg-base-100 shadow-sm hover:bg-base-100' : 'bg-transparent text-base-content/70 hover:bg-base-300'}`}
                onClick={() => {
                  dispatch(setFilters({ isActive: filters.isActive === true ? undefined : true }));
                  dispatch(setCurrentPage(1));
                  dispatch(fetchCoursesWithFiltersAsyncThunk());
                }}
              >
                Active
              </button>
              <button 
                className={`btn btn-sm border-none ${filters.isActive === false ? 'bg-base-100 shadow-sm hover:bg-base-100' : 'bg-transparent text-base-content/70 hover:bg-base-300'}`}
                onClick={() => {
                  dispatch(setFilters({ isActive: filters.isActive === false ? undefined : false }));
                  dispatch(setCurrentPage(1));
                  dispatch(fetchCoursesWithFiltersAsyncThunk());
                }}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>

        {/* Courses Grid */}
        {courses.length === 0 && !isLoading ? (
          <div className="bg-base-100 rounded-xl shadow-sm p-12 text-center">
            <div className="text-base-content/50 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-base-content mb-2">No Courses Found</h3>
            <p className="text-base-content/70 mb-6">Get started by creating your first course.</p>
            <button 
              className="btn btn-primary text-base-100"
              onClick={handleCreateCourse}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create First Course
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
            {courses.map((course) => (
              <div key={course._id} className="bg-base-100 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden flex flex-col h-full">
                {/* Background Image - Fixed Height */}
                <div className="relative w-full h-48 overflow-hidden flex-shrink-0">
                  {course.thumbnailUrl ? (
                    <>
                      <img
                        src={course.thumbnailUrl}
                        alt={course.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div className="hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-primary/30 to-secondary/30">
                        <svg className="w-16 h-16 text-base-content/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <svg className="w-24 h-24 text-base-content/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Overlay Card with Course Details */}
                  <div className="absolute bottom-0 left-0 right-0 bg-base-100/95 backdrop-blur-sm rounded-t-xl p-3 border-t border-base-300 shadow-lg">
                    {/* Course Header */}
                    <div className="mb-2 min-w-0">
                      <h3 className="text-sm font-bold text-base-content mb-1.5 line-clamp-1">{course.title || "(No Name)"}</h3>
                      {/* Price, Duration on left, Badges on right */}
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        {/* Left side: Price and Duration */}
                        <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                          <span className="text-xs sm:text-sm font-bold text-primary">{formatPrice(course.price)}</span>
                          <span className="text-[9px] sm:text-[10px] text-base-content/70">•</span>
                          <span className="text-[9px] sm:text-[10px] text-base-content/70">{formatDuration(course.duration)}</span>
                        </div>
                        {/* Right side: Active and Level badges */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <div className={`badge px-1 sm:px-1.5 py-0.5 text-[7px] sm:text-[8px] font-medium ${course.isActive ? 'badge-success' : 'badge-error'}`}>
                            <span className="font-normal whitespace-nowrap">{course.isActive ? 'Active' : 'Inactive'}</span>
                          </div>
                          <div className={`badge px-1 sm:px-1.5 py-0.5 text-[7px] sm:text-[8px] font-medium ${getLevelColor(course.level)}`}>
                            <span className="font-normal whitespace-nowrap">{course.level}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Course Details */}
                    <div className="grid grid-cols-3 gap-1.5 text-[10px]">
                      <div className="text-center p-1.5 bg-base-200/50 rounded overflow-hidden">
                        <div className="text-base-content/70 mb-0.5 text-[9px] truncate">Categories</div>
                        <div className="text-base-content font-semibold truncate">{course.categories?.length || 0}</div>
                      </div>
                      <div className="text-center p-1.5 bg-base-200/50 rounded overflow-hidden">
                        <div className="text-base-content/70 mb-0.5 text-[9px] truncate">CPD</div>
                        <div className="text-base-content font-semibold truncate">{course.cpdPoints}</div>
                      </div>
                      <div className="text-center p-1.5 bg-base-200/50 rounded overflow-hidden">
                        <div className="text-base-content/70 mb-0.5 text-[9px] truncate">Time</div>
                        <div className="text-base-content font-semibold truncate text-[9px]">{formatDuration(course.duration)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-3 bg-base-100 flex-shrink-0 mt-auto">
                  <div className="flex gap-2">
                    <button
                      className="btn btn-sm btn-outline btn-primary flex-1 flex-nowrap min-w-0 px-2"
                      onClick={() => handleViewDetails(course)}
                    >
                      <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span className="truncate">View</span>
                    </button>
                    <button
                      className="btn btn-sm btn-outline btn-secondary flex-1 flex-nowrap min-w-0 px-2"
                      onClick={() => handleEditCourse(course._id)}
                    >
                      <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span className="truncate">Edit</span>
                    </button>
                    <button
                      className="btn btn-sm btn-outline btn-error flex-shrink-0 px-2"
                      onClick={() => handleDeleteCourse(course)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Component */}
        {courses.length > 0 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
            showPageSizeSelector={true}
            showItemCount={true}
            className="mt-6"
          />
        )}
      </div>
    </div>
  );
}

export default Courses;
